import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import CountUp from 'react-countup';
import { FaAward, FaUsers, FaProjectDiagram, FaHeart } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import AboutModal from './AboutModal';

const AboutSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const stats = [
    { icon: FaProjectDiagram, value: 500, suffix: '+', label: t('about.stat1Label') },
    { icon: FaUsers, value: 10, suffix: 'K+', label: t('about.stat2Label') },
    { icon: FaAward, value: 10, suffix: '+', label: t('about.stat3Label') },
    { icon: FaHeart, value: 99, suffix: '%', label: t('about.stat4Label') },
  ];

  return (
    <section id="about" ref={ref} className="section-padding bg-gradient-to-b from-[#0a0e27] to-[#0f1640]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Images Grid - Order 2 on mobile, Order 1 on desktop */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative order-2 md:order-1"
          >
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="glass-strong rounded-2xl overflow-hidden h-64 glow-hover"
              >
                <img
                  src="https://images.unsplash.com/photo-1608377205619-03a0b4c4e270?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxob21lJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzI2MzM3OTd8MA&ixlib=rb-4.1.0"
                  alt="Smart Control"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                className="glass-strong rounded-2xl overflow-hidden h-64 mt-8 glow-hover"
              >
                <img
                  src="https://images.unsplash.com/photo-1558002038-1055907df827?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGhvbWV8ZW58MHx8fHwxNzcyNjMzNzkzfDA&ixlib=rb-4.1.0"
                  alt="Smart Security"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                className="glass-strong rounded-2xl overflow-hidden h-64 -mt-8 glow-hover"
              >
                <img
                  src="https://images.unsplash.com/photo-1614696131965-cc559b1ecad1?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MjYzMzgxM3ww&ixlib=rb-4.1.0"
                  alt="Smart Lighting"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="glass-strong rounded-2xl overflow-hidden h-64 glow-hover"
              >
                <img
                  src="https://images.unsplash.com/photo-1519558260268-cde7e03a0152?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGhvbWV8ZW58MHx8fHwxNzcyNjMzNzkzfDA&ixlib=rb-4.1.0"
                  alt="Home Automation"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>

            {/* Floating Element with CountUp */}
            <motion.div
              animate={{ y: [0, -20, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="absolute -bottom-6 -right-6 glass-strong rounded-2xl p-6 glow"
            >
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text">
                  {isInView && <CountUp start={0} end={10} duration={2} />}+
                </div>
                <div className="text-white/80 text-sm mt-1">{t('about.yearsLeading')}</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Content - Order 1 on mobile, Order 2 on desktop */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="order-1 md:order-2"
          >
            <div className="inline-block glass px-6 py-2 rounded-full mb-6">
              <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
                {t('about.badge')}
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {t('about.title')}
              <span className="gradient-text block mt-2">{t('about.since')}</span>
            </h2>

            <p className="text-white/80 text-lg mb-8 leading-relaxed">
              {t('about.description')}
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                t('about.feature1'),
                t('about.feature2'),
                t('about.feature3'),
                t('about.feature4'),
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <div className="w-2 h-2 bg-[#00d4ff] rounded-full glow" />
                  <span className="text-white/80">{feature}</span>
                </motion.div>
              ))}
            </div>

            <button 
              onClick={() => setIsAboutModalOpen(true)}
              className="btn-primary"
            >
              {t('about.learnMore')}
            </button>
          </motion.div>
        </div>

        {/* Stats with CountUp Animation */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05, y: -5 }}
              className="glass-strong rounded-2xl p-6 text-center glow-hover"
            >
              <stat.icon className="text-4xl text-[#00d4ff] mx-auto mb-4" />
              <div className="text-3xl font-bold text-white mb-2">
                {isInView && (
                  <CountUp 
                    start={0} 
                    end={stat.value} 
                    duration={2.5}
                    delay={index * 0.2}
                  />
                )}
                {stat.suffix}
              </div>
              <div className="text-white/60 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* About Modal */}
      <AboutModal
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
      />
    </section>
  );
};

export default AboutSection;
